import { IDocumentVersion } from '../models/document-version.model';

export interface IDocumentVersionDisambiguator {
  created?: string | Date;
  majorNumber?: string | number;
}

export function versionCreatedKey(d: Date | string | undefined | null): string {
  if (d == null || d === '') {
    return '';
  }
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString();
}

function isSameVersionRow(a: IDocumentVersion, b: IDocumentVersion): boolean {
  if (a.id !== b.id) {
    return false;
  }
  if (versionCreatedKey(a.created) !== versionCreatedKey(b.created)) {
    return false;
  }
  return String(a.majorNumber ?? '') === String(b.majorNumber ?? '');
}

/** When several versions share the same `id`, pick the row that matches the disambiguator, else the first. */
export function selectMatchingDocumentVersion(
  versions: IDocumentVersion[] | undefined | null,
  documentVersionId: string,
  disambiguator?: IDocumentVersionDisambiguator
): IDocumentVersion | undefined {
  const list = versions ?? [];
  const candidates = list.filter((v) => v.id === documentVersionId);
  if (candidates.length === 0) {
    return undefined;
  }
  if (candidates.length === 1) {
    return candidates[0];
  }
  if (disambiguator?.created != null) {
    const key = versionCreatedKey(disambiguator.created);
    const byCreated = candidates.find((c) => versionCreatedKey(c.created) === key);
    if (byCreated) {
      return byCreated;
    }
  }
  if (disambiguator?.majorNumber !== undefined && disambiguator?.majorNumber !== null) {
    const want = String(disambiguator.majorNumber);
    const byMajor = candidates.find((c) => String(c.majorNumber) === want);
    if (byMajor) {
      return byMajor;
    }
  }
  return candidates[0];
}

export function findDocumentVersionIndex(
  versions: IDocumentVersion[],
  documentVersionId: string,
  disambiguator?: IDocumentVersionDisambiguator
): number {
  const match = selectMatchingDocumentVersion(versions, documentVersionId, disambiguator);
  if (!match) {
    return -1;
  }
  return versions.findIndex((v) => isSameVersionRow(v, match));
}
